import { useEffect, useMemo, useRef, useState } from 'react';

import { Badge } from 'cpool-components/general/Badge';
import { DataTable, DataTableColumn } from 'cpool-components/general/DataTable';
import { Truncate } from 'cpool-components/general/Truncate';
import { Checkbox } from 'cpool-components/input/Checkbox';
import { OFFER_ACCEPTANCE_STATUSES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { OfferRow } from './OfferRow';
import { OffersCollapsible } from './OffersCollapsible';
import { useAcceptedOffers } from '../leadDetails/components/Overview/OfferManagement/hooks/useAcceptedOffers';

type Props = {
    hasSecondApplicant?: boolean;
    leadId: string;
    name: string;
    preSelectedOfferId?: string;
};

type OfferWithOrder = SchemaLoanRequestResolver_GetAcceptedOffers[number] & { order: number };

export const OffersSelect: React.FC<Props> = ({ hasSecondApplicant = false, leadId, name, preSelectedOfferId }) => {
    const { t } = useTranslation();
    const { setValue, watch } = useFormContext();
    const { data: offers } = useAcceptedOffers(leadId);
    const [orderedOffers, setOrderedOffers] = useState<OfferWithOrder[]>([]);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    const successOffers = useMemo(
        () => offers?.filter((offer) => offer.acceptanceStatus === OFFER_ACCEPTANCE_STATUSES.SUCCESS) ?? [],
        [offers],
    );

    useEffect(() => {
        if (successOffers.length > 0) {
            const withOrder = successOffers.map((offer, index) => ({
                ...offer,
                order: index,
            }));
            setOrderedOffers(withOrder);
        }
    }, [successOffers]);

    const appliedPreSelectionRef = useRef<string | undefined>(undefined);

    // apply the pre-selection once; later refetches/reorders must not reset the user's selection
    useEffect(() => {
        if (preSelectedOfferId && appliedPreSelectionRef.current !== preSelectedOfferId && orderedOffers.length > 0) {
            const offer = orderedOffers.find((o) => o.id === preSelectedOfferId);
            if (offer) {
                appliedPreSelectionRef.current = preSelectedOfferId;
                setValue(name, [offer.id]);
            }
        }
    }, [preSelectedOfferId, orderedOffers, name, setValue]);

    const selectedOfferIds = (watch(name) as string[] | undefined) ?? [];

    const handleCheckboxChange = (offerId: string, checked: boolean) => {
        if (checked) {
            const newSelectedIds = orderedOffers
                .filter((offer) => selectedOfferIds.includes(offer.id) || offer.id === offerId)
                .map((offer) => offer.id);
            setValue(name, newSelectedIds);
        } else {
            setValue(
                name,
                selectedOfferIds.filter((id) => id !== offerId),
            );
        }
    };

    const handleAllCheckboxesChange = (checked: boolean) => {
        setValue(name, checked ? orderedOffers.map((offer) => offer.id) : []);
    };

    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent<HTMLTableRowElement>, dropIndex: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) return;

        const newOffers = [...orderedOffers];
        const [draggedItem] = newOffers.splice(draggedIndex, 1);
        newOffers.splice(dropIndex, 0, draggedItem);

        const reordered = newOffers.map((offer, index) => ({
            ...offer,
            order: index,
        }));

        setOrderedOffers(reordered);
        setDraggedIndex(null);

        const reorderedIds = reordered.filter((offer) => selectedOfferIds.includes(offer.id)).map((offer) => offer.id);
        setValue(name, reorderedIds);
    };

    if (successOffers.length === 0) {
        return (
            <div className="w-full rounded-md border-l-2 border-blue bg-white p-2.5">
                <p className="mb-2.5 flex h-[25px] items-center pl-1.5 text-sm font-medium text-midnight">
                    {t('MESSAGE.EMAIL.SELECT_OFFER')}
                </p>
                <p className="rounded border border-grey-outline-light px-3 py-2 text-sm text-midnight">
                    {t('MESSAGE.EMAIL.NO_OFFER')}
                </p>
            </div>
        );
    }

    const areAllOffersSelected =
        orderedOffers.length > 0 && orderedOffers.every((offer) => selectedOfferIds.includes(offer.id));

    const truncatedHeader = (label: string) => <Truncate maxWidth="100%">{label}</Truncate>;

    return (
        <OffersCollapsible
            title={t('MESSAGE.EMAIL.SELECT_OFFER')}
            badge={<Badge>{t('MESSAGE.EMAIL.SELECTED_OFFERS', { count: selectedOfferIds.length })}</Badge>}
        >
            <DataTable className="w-full [&>table]:table-fixed [&_th>div]:min-w-0 [&_th>div]:font-medium [&_th>div>span]:block [&_th>div>span]:min-w-0 [&_th:nth-child(1)]:w-[5%] [&_th:nth-child(1)]:px-0 [&_th:nth-child(2)]:w-[15%] [&_th:nth-child(3)]:w-[12.5%] [&_th:nth-child(4)]:w-[12.5%] [&_th:nth-child(5)]:w-[12.5%] [&_th:nth-child(6)]:w-[12.5%] [&_th:nth-child(7)]:w-[9.5%] [&_th:nth-child(8)]:w-[11.5%] [&_th:nth-child(9)]:w-[8.5%]">
                <DataTableColumn
                    name={
                        <>
                            <Checkbox
                                id="email-offers-select-all"
                                aria-hidden="true"
                                checked={areAllOffersSelected}
                                onChange={(event) => handleAllCheckboxesChange(event.target.checked)}
                                tabIndex={-1}
                            />
                            <span className="sr-only">{t('MESSAGE.EMAIL.SELECT_OFFER')}</span>
                        </>
                    }
                    field="selected"
                    align="center"
                />
                <DataTableColumn
                    name={truncatedHeader(t('OFFER_MANAGEMENT.HEADERS.BANK'))}
                    field="bankLogo"
                    align="center"
                />
                <DataTableColumn name={truncatedHeader(t('OFFER_MANAGEMENT.HEADERS.ACCEPTED_AT'))} field="acceptedAt" />
                <DataTableColumn
                    name={truncatedHeader(t('OFFER_MANAGEMENT.HEADERS.APPLICATION_NUMBER'))}
                    field="providerApplicationNumber"
                />
                <DataTableColumn
                    name={truncatedHeader(t('OFFER_MANAGEMENT.HEADERS.CREDIT_AMOUNT'))}
                    field="creditAmount"
                />
                <DataTableColumn name={truncatedHeader(t('OFFER_MANAGEMENT.HEADERS.RUNTIME'))} field="runtime" />
                <DataTableColumn name={truncatedHeader(t('OFFER_MANAGEMENT.HEADERS.RATE'))} field="rate" />
                <DataTableColumn name={truncatedHeader(t('OFFER_MANAGEMENT.HEADERS.INTEREST'))} field="interest" />
                <DataTableColumn
                    name={truncatedHeader(t('OFFER_MANAGEMENT.HEADERS.RESIDUAL_DEBT_INSURANCE'))}
                    field="applicant1ResidualDebtInsurance"
                />
                {orderedOffers.map((offer, index) => (
                    <OfferRow
                        key={offer.id}
                        payload={offer}
                        offer={offer}
                        hasSecondApplicant={hasSecondApplicant}
                        index={index}
                        isSelected={selectedOfferIds.includes(offer.id)}
                        onCheckboxChange={handleCheckboxChange}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    />
                ))}
            </DataTable>
        </OffersCollapsible>
    );
};
